import React from "react";
import { styled } from "@glitz/react";
import Button from "./basic/Button";
import Checkbox from "./basic/Checkbox";
import {
  fontSize,
  fontWeight,
  spacing,
  black,
  darkGrey,
  mediumGrey,
  grey,
  green,
  white,
  red,
  Size,
  Theme,
} from "../theme";

export type CyclingBottlePlannerProps = {
  /** Total mängd kh från sportdryck över hela loppet (g). */
  totalDryckG: number;
  /** Banlängd i km. Krävs för att etapp-fördelning ska visas. */
  distanceKm: number | null;
  /** Antal flaskor som ska blandas per etapp (fast = 2 just nu). */
  bottleCount: number;
  useOwnStops: boolean;
  onUseOwnStopsChange: (next: boolean) => void;
  /** Sorterad stigande lista över stopp i km från start. */
  stopsKm: number[];
  onStopsKmChange: (next: number[]) => void;
  /**
   * Föreslagna standardstopp för aktuell distans (t.ex. Vätternrundan:
   * Fagerhult 133, Karlsborg 204). Visas som snabbknappar och fylls
   * automatiskt när toggle slås på första gången om listan är tom.
   */
  suggestedStops?: { km: number; label: string }[];
};

type Leg = {
  index: number;
  startKm: number;
  endKm: number;
  km: number;
  kh: number;
  perBottle: number;
};

function formatKm(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1).replace(/\.0$/, "");
}

function buildLegs(
  totalDryckG: number,
  distanceKm: number,
  stopsKm: number[],
  bottleCount: number
): Leg[] {
  if (distanceKm <= 0 || totalDryckG <= 0) return [];
  const cleanStops = stopsKm
    .filter((s) => Number.isFinite(s) && s > 0 && s < distanceKm)
    .map((s) => Math.round(s * 10) / 10);
  const uniq = Array.from(new Set(cleanStops)).sort((a, b) => a - b);
  const breakpoints = [0, ...uniq, distanceKm];
  const gPerKm = totalDryckG / distanceKm;
  const legs: Leg[] = [];
  for (let i = 0; i < breakpoints.length - 1; i++) {
    const startKm = breakpoints[i]!;
    const endKm = breakpoints[i + 1]!;
    const km = Math.max(0, endKm - startKm);
    const kh = Math.round(km * gPerKm);
    const perBottle = bottleCount > 0 ? Math.round(kh / bottleCount) : kh;
    legs.push({ index: i + 1, startKm, endKm, km, kh, perBottle });
  }
  return legs;
}

const CyclingBottlePlanner: React.FC<CyclingBottlePlannerProps> = ({
  totalDryckG,
  distanceKm,
  bottleCount,
  useOwnStops,
  onUseOwnStopsChange,
  stopsKm,
  onStopsKmChange,
  suggestedStops = [],
}) => {
  const handleToggle = (next: boolean) => {
    if (next && stopsKm.length === 0 && suggestedStops.length > 0 && distanceKm != null) {
      const valid = suggestedStops
        .map((s) => s.km)
        .filter((km) => km > 0 && km < distanceKm)
        .sort((a, b) => a - b);
      if (valid.length > 0) onStopsKmChange(valid);
    }
    onUseOwnStopsChange(next);
  };

  const handleAddSuggested = (km: number) => {
    if (distanceKm == null || km <= 0 || km >= distanceKm) return;
    if (stopsKm.includes(km)) return;
    const next = [...stopsKm, km].sort((a, b) => a - b);
    onStopsKmChange(next);
  };
  const distOk = distanceKm != null && distanceKm > 0;
  const legs =
    distOk && useOwnStops
      ? buildLegs(totalDryckG, distanceKm!, stopsKm, bottleCount)
      : [];

  const baseLeg: Leg | null = distOk
    ? {
        index: 1,
        startKm: 0,
        endKm: distanceKm!,
        km: distanceKm!,
        kh: Math.round(totalDryckG),
        perBottle:
          bottleCount > 0
            ? Math.round(totalDryckG / bottleCount)
            : Math.round(totalDryckG),
      }
    : null;

  const handleAddStop = () => {
    if (!distOk) return;
    const last = stopsKm[stopsKm.length - 1] ?? 0;
    const suggested = Math.min(
      Math.round((last + (distanceKm! - last) / 2) * 10) / 10,
      Math.max(1, distanceKm! - 1)
    );
    onStopsKmChange([...stopsKm, suggested]);
  };

  const handleRemoveStop = (idx: number) => {
    onStopsKmChange(stopsKm.filter((_, i) => i !== idx));
  };

  const handleStopChange = (idx: number, raw: string) => {
    const v = parseFloat(raw.replace(",", "."));
    const next = stopsKm.slice();
    if (!Number.isFinite(v)) {
      next[idx] = 0;
    } else {
      next[idx] = v;
    }
    onStopsKmChange(next);
  };

  const handleStopBlur = () => {
    if (!distOk) return;
    const cleaned = stopsKm
      .filter((s) => Number.isFinite(s) && s > 0 && s < distanceKm!)
      .map((s) => Math.round(s * 10) / 10);
    const uniq = Array.from(new Set(cleaned)).sort((a, b) => a - b);
    const same =
      uniq.length === stopsKm.length &&
      uniq.every((v, i) => v === stopsKm[i]);
    if (!same) onStopsKmChange(uniq);
  };

  if (totalDryckG <= 0) return null;

  return (
    <Wrap>
      <Header>
        <BlockTitle>Flaskor & depåer</BlockTitle>
        <ToggleRow>
          <Checkbox
            label="Jag har egna stopp/depåer"
            checked={useOwnStops}
            onChange={handleToggle}
          />
        </ToggleRow>
      </Header>

      <Summary>
        Totalt <strong>{Math.round(totalDryckG)} g kh</strong> via sportdryck
        {distOk ? (
          <>
            {" "}över <strong>{formatKm(distanceKm!)} km</strong>
          </>
        ) : null}
        .
      </Summary>

      {!distOk && (
        <Hint>
          Ange en banlängd (km) för att räkna ut hur mycket kolhydrater som
          ska blandas i flaskorna mellan stoppen.
        </Hint>
      )}

      {distOk && useOwnStops && (
        <StopsBlock>
          <StopsLabel>Stopp (km från start)</StopsLabel>
          {stopsKm.length === 0 ? (
            <StopsMuted>Inga stopp tillagda än.</StopsMuted>
          ) : (
            <StopsList>
              {stopsKm.map((km, i) => (
                <StopRow key={i}>
                  <StopInput
                    type="number"
                    inputMode="decimal"
                    min={0.1}
                    max={(distanceKm ?? 0) - 0.1}
                    step={1}
                    value={Number.isFinite(km) ? km : ""}
                    onChange={(e) => handleStopChange(i, e.target.value)}
                    onBlur={handleStopBlur}
                  />
                  <StopUnit>km</StopUnit>
                  <RemoveBtn
                    type="button"
                    onClick={() => handleRemoveStop(i)}
                    aria-label={`Ta bort stopp ${i + 1}`}
                  >
                    ×
                  </RemoveBtn>
                </StopRow>
              ))}
            </StopsList>
          )}
          {distOk && suggestedStops.length > 0 && (
            <SuggestedRow>
              <SuggestedLabel>Förslag:</SuggestedLabel>
              {suggestedStops
                .filter((s) => s.km > 0 && s.km < distanceKm! && !stopsKm.includes(s.km))
                .map((s) => (
                  <SuggestedChip
                    key={s.km}
                    type="button"
                    onClick={() => handleAddSuggested(s.km)}
                  >
                    + {s.label} ({s.km} km)
                  </SuggestedChip>
                ))}
            </SuggestedRow>
          )}
          <AddStopRow>
            <Button
              as={Theme.Dark}
              size={Size.Small}
              onClick={handleAddStop}
            >
              + Lägg till eget stopp
            </Button>
          </AddStopRow>
        </StopsBlock>
      )}

      <LegsList>
        {(useOwnStops && distOk ? legs : baseLeg ? [baseLeg] : []).map(
          (leg) => (
            <LegRow key={`leg-${leg.index}`}>
              <LegHeader>
                <LegBadge>Etapp {leg.index}</LegBadge>
                <LegRange>
                  {formatKm(leg.startKm)}–{formatKm(leg.endKm)} km
                </LegRange>
                <LegDist>({formatKm(leg.km)} km)</LegDist>
              </LegHeader>
              <LegBody>
                <LegStat>
                  <LegStatLabel>Totalt i etapp</LegStatLabel>
                  <LegStatValue>{leg.kh} g kh</LegStatValue>
                </LegStat>
                <LegStat>
                  <LegStatLabel>Per flaska ({bottleCount} st)</LegStatLabel>
                  <LegStatValue>{leg.perBottle} g kh</LegStatValue>
                </LegStat>
              </LegBody>
            </LegRow>
          )
        )}
      </LegsList>
    </Wrap>
  );
};

export default CyclingBottlePlanner;

const Wrap = styled.div({
  marginTop: spacing.l,
  marginBottom: spacing.l,
  padding: { xy: spacing.m },
  backgroundColor: mediumGrey,
  borderRadius: "12px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const Header = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.s,
  marginBottom: spacing.s,
});

const BlockTitle = styled.h4({
  fontSize: fontSize.l,
  fontWeight: fontWeight.heavy,
  color: black,
  margin: { xy: 0 },
});

const ToggleRow = styled.div({
  display: "flex",
  alignItems: "center",
});

const Summary = styled.p({
  fontSize: fontSize.s,
  color: darkGrey,
  margin: { xy: 0 },
  marginBottom: spacing.s,
  lineHeight: 1.5,
});

const Hint = styled.p({
  fontSize: fontSize.xs,
  color: darkGrey,
  fontStyle: "italic",
  margin: { xy: 0 },
  marginBottom: spacing.s,
});

const StopsBlock = styled.div({
  backgroundColor: white,
  borderRadius: "8px",
  padding: { xy: spacing.s },
  marginBottom: spacing.s,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const StopsLabel = styled.div({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: spacing.xs,
});

const StopsList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  marginBottom: spacing.xs,
});

const StopsMuted = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
  fontStyle: "italic",
  marginBottom: spacing.xs,
});

const StopRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const StopInput = styled.input({
  width: "90px",
  padding: { xy: spacing.s },
  fontSize: fontSize.s,
  border: { xy: { style: "solid", color: black, width: "1px" } },
  borderRadius: "4px",
  backgroundColor: white,
  color: black,
  transitionProperty: "border-color",
  transitionDuration: "0.2s",
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
});

const StopUnit = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
  fontWeight: fontWeight.bold,
});

const RemoveBtn = styled.button({
  width: "28px",
  height: "28px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  backgroundColor: white,
  color: red,
  fontSize: fontSize.m,
  fontWeight: fontWeight.heavy,
  borderRadius: "50%",
  cursor: "pointer",
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transitionProperty: "background-color, border-color, color",
  transitionDuration: "0.15s",
  ":hover": {
    backgroundColor: red,
    color: white,
    border: { xy: { style: "solid", color: red, width: "1px" } },
  },
});

const AddStopRow = styled.div({
  marginTop: spacing.xs,
  display: "flex",
});

const SuggestedRow = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: spacing.xs,
  marginTop: spacing.xs,
  marginBottom: spacing.xs,
});

const SuggestedLabel = styled.span({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
});

const SuggestedChip = styled.button({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: black,
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "8px",
  padding: { x: spacing.s, y: "6px" },
  cursor: "pointer",
  transitionProperty: "background-color, border-color, color",
  transitionDuration: "0.15s",
  ":hover": {
    backgroundColor: black,
    color: white,
    border: { xy: { style: "solid", color: black, width: "1px" } },
  },
});

const LegsList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const LegRow = styled.div({
  backgroundColor: white,
  borderRadius: "8px",
  padding: { xy: spacing.s },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const LegHeader = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: spacing.xs,
  marginBottom: spacing.xs,
});

const LegBadge = styled.span({
  display: "inline-block",
  backgroundColor: black,
  color: white,
  fontSize: "10px",
  fontWeight: fontWeight.heavy,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  padding: { x: spacing.xs, y: "3px" },
  borderRadius: "4px",
});

const LegRange = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
});

const LegDist = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
});

const LegBody = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: spacing.s,
});

const LegStat = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
});

const LegStatLabel = styled.div({
  fontSize: "10px",
  color: darkGrey,
  fontWeight: fontWeight.bold,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
});

const LegStatValue = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.heavy,
  color: black,
});
